import {
  FC,
  memo,
  useState,
  useEffect,
  useCallback,
  ChangeEvent,
  useMemo,
} from 'react';
import countryList from 'country-list';
import { format } from 'date-fns';
import styled from 'styled-components';
import {
  useForm,
  SubmitHandler,
  UseFormSetValue,
  Resolver,
  UseFormSetError,
  FieldValues,
} from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import theme from 'theme';
import { useFormErrors } from '@hooks/useFormErrors';
import { useCurrentUser } from '@redux/selectors/users';
import { useUpdateUserDataMutation } from '@redux/queries/user';
import { username } from '@utils/constants/schemas';
import { BIRTHDAY_FORMAT } from '@utils/constants';
import {
  TEXT_INPUT_LABELS,
  TEXT_INPUT_PLACEHOLDERS,
} from '@utils/constants/form';
import TextInput from '@components/fields/input';
import {
  StyledButton,
  StyledErrorContainer,
  StyledErrorText,
} from '@components/form/styled';
import Select from '@components/fields/select';
import DatePicker from '@components/fields/datepicker';

import TeamsSelect from './components/TeamsSelect';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

type Props = {
  buttonText: string;
  onSuccess: () => void;
};

type SetUpProfileState = {
  username: string;
  name: string;
  bio?: string;
  team: number;
  country?: string;
  birthday: string;
};

const schema = yup.object().shape({
  username,
  name: yup.string().required('Display name is required'),
  bio: yup
    .string()
    .max(100, 'Bio could not be longer than 100 symbols')
    .nullable(),
  team: yup.number().required('Team is required'),
  country: yup.string().nullable(),
  birthday: yup.string().required('Date of birth is required'),
});

const SetUpProfile: FC<Props> = ({
  buttonText = 'Save Changes',
  onSuccess,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const user = useCurrentUser();
  const countries = useMemo(() => countryList.getData() || [], []);
  const [updateUserData, { isLoading, isSuccess, isError, error }] =
    useUpdateUserDataMutation();

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    setError,
    watch,
    formState: { errors },
  } = useForm<SetUpProfileState>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      username: user?.username || '',
      name: user?.name || '',
      bio: user?.bio || '',
      team: user?.team?.id,
      country: user?.country || '',
      birthday: user?.birthday || '',
    },
    resolver: yupResolver(schema) as Resolver<SetUpProfileState>,
  });

  const handleChange = useCallback(
    ({
      field,
      text,
      onChange,
    }: {
      field: keyof SetUpProfileState;
      text: string | number;
      onChange: UseFormSetValue<SetUpProfileState>;
    }) => {
      clearErrors(field);
      onChange(field, text);

      if (errorMessage) {
        setErrorMessage('');
      }
    },
    [clearErrors, errorMessage],
  );

  const onSubmitForm: SubmitHandler<SetUpProfileState> = (
    data: SetUpProfileState,
  ) => {
    const { name, username, bio, team, country, birthday } = data;

    updateUserData({
      name,
      username,
      bio,
      team,
      country,
      birthday: birthday ? format(birthday, BIRTHDAY_FORMAT) : undefined,
    });
  };

  useFormErrors({
    isError,
    error,
    setError: setError as UseFormSetError<FieldValues>,
    setErrorMessage,
    errorMessage: 'Invalid data',
  });

  useEffect(() => {
    if (isSuccess) {
      onSuccess();
    }
  }, [isSuccess, onSuccess]);

  return (
    <Container>
      <FormContainer>
        <TextInput
          placeholder={TEXT_INPUT_PLACEHOLDERS.USERNAME}
          label={TEXT_INPUT_LABELS.USERNAME}
          prefix='@'
          errorText={errors.username?.message}
          {...register('username')}
          value={watch('username')}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            handleChange({
              field: 'username',
              text: e.target.value,
              onChange: setValue,
            })
          }
        />

        <TextInput
          placeholder={TEXT_INPUT_PLACEHOLDERS.NAME}
          label={TEXT_INPUT_LABELS.NAME}
          errorText={errors.name?.message}
          {...register('name')}
          value={watch('name')}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            handleChange({
              field: 'name',
              text: e.target.value,
              onChange: setValue,
            })
          }
        />

        <TextInput
          placeholder={TEXT_INPUT_PLACEHOLDERS.BIO}
          label={TEXT_INPUT_LABELS.BIO}
          errorText={errors.bio?.message}
          {...register('bio')}
          value={watch('bio')}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            handleChange({
              field: 'bio',
              text: e.target.value,
              onChange: setValue,
            })
          }
        />

        <TeamsSelect<SetUpProfileState>
          value={watch('team')}
          handleChange={handleChange}
          setValue={setValue}
          errorText={errors.team?.message}
        />

        <Select
          id='country'
          label={TEXT_INPUT_LABELS.COUNTRY}
          placeholder={TEXT_INPUT_PLACEHOLDERS.COUNTRY}
          options={countries.map((country) => ({
            label: country.name,
            value: country.code,
          }))}
          value={
            watch('country')
              ? {
                  value: watch('country'),
                  label: countries.find(
                    (country) => country.code === watch('country'),
                  )?.name,
                }
              : undefined
          }
          {...register('country')}
          onChange={(newValue: unknown) =>
            handleChange({
              field: 'country',
              text: (newValue as { value: string }).value,
              onChange: setValue,
            })
          }
          errorText={errors.country?.message}
        />

        <DatePicker
          label={TEXT_INPUT_LABELS.BIRTHDAY}
          value={watch('birthday')}
          onChange={(date) =>
            handleChange({
              field: 'birthday',
              text: date,
              onChange: setValue,
            })
          }
          errorText={errors.birthday?.message}
        />
      </FormContainer>

      <StyledErrorContainer>
        {errorMessage && <StyledErrorText>{errorMessage}</StyledErrorText>}
      </StyledErrorContainer>

      <StyledButton
        onClick={handleSubmit(onSubmitForm)}
        disabled={isLoading || Object.keys(errors).length > 0}
        isLoading={isLoading}
        type='submit'
        textStyle={{ fontSize: theme.fonts.fontSize.xl }}
      >
        {buttonText}
      </StyledButton>
    </Container>
  );
};

export default memo(SetUpProfile);
